import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../../assets/css/Urls.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { listUrls, refreshUrl, deleteUrl } from "../../services/urlService";
import { getProjectPid } from "../../utils/sessionProject";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";

const statusOptions = [
  "Pending",
  "Ready to Crawl",
  "Reading URL",
  "Ready",
  "Generating Document",
];

const itemsPerPage = 100;

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "start-ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "start-ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "end-ellipsis",
    totalPages,
  ];
};

const getStatusMeta = (item) => {
  if (item.err_msg || item.status === "Failed") {
    return {
      key: "failed",
      label: "Failed",
      icon: "fa-circle-exclamation",
      className: "url-status-failed",
    };
  }

  if (item.status === "Completed" || item.status === "Ready") {
    return {
      key: item.status,
      label: item.status,
      icon: "fa-circle-check",
      className: "url-status-ready",
    };
  }

  const statusClassMap = {
    Pending: "url-status-pending",
    "Ready to Crawl": "url-status-crawl",
    "Reading URL": "url-status-reading",
    "Generating Document": "url-status-generating",
  };

  return {
    key: item.status || "In Process",
    label: item.status || "In Process",
    icon: "fa-clock",
    className: statusClassMap[item.status] || "url-status-progress",
  };
};

const UrlList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchRef = useRef("");
  const searchDebounceRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [urlListData, setUrlListData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState([]);
  const [deletingIds, setDeletingIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const paginationItems = getPaginationItems(currentPage, totalPages);

  const fetchUrls = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const projectPid = getProjectPid();
      const res = await listUrls({ projectPid, page, limit: itemsPerPage, search, crawlStatus: '' });
      const items = res?.response?.items ?? res?.response?.urls ?? res?.response?.data ?? [];
      const count = res?.response?.pagination?.total ?? res?.response?.totalCount ?? res?.response?.total ?? items.length;
      setUrlListData(Array.isArray(items) ? items : []);
      setTotalCount(count);
    } catch (err) {
      console.error('Failed to fetch URLs', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Page change — read latest search from ref
  useEffect(() => {
    fetchUrls(currentPage, searchRef.current);
  }, [currentPage, fetchUrls]);

  const paginatedUrls = urlListData;

  const handleRefreshOne = async (urlPid) => {
    setRefreshingIds((ids) => [...ids, urlPid]);
    try {
      await refreshUrl({ urlPid });
      toast.success("URL queued for refresh");
      fetchUrls(currentPage, searchRef.current);
    } catch (err) {
      console.error("Refresh URL failed", err);
      toast.error(err?.response?.data?.message || "Failed to refresh URL");
    } finally {
      setRefreshingIds((ids) => ids.filter((id) => id !== urlPid));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { url_pid: urlPid } = deleteTarget;
    setDeletingIds((ids) => [...ids, urlPid]);
    try {
      await deleteUrl({ urlPid });
      toast.success("URL deleted");
      setDeleteTarget(null);
      fetchUrls(currentPage, searchRef.current);
    } catch (err) {
      console.error("Delete URL failed", err);
      toast.error(err?.response?.data?.message || "Failed to delete URL");
    } finally {
      setDeletingIds((ids) => ids.filter((id) => id !== urlPid));
    }
  };

  return (
    <main className="app-wrapper url-list-page fill-table-page">
      <div className="container-fluid">
        <section className="url-list-toolbar p-3 mb-3">
          <div className="url-list-actions">
            <div className="url-filter-group">
              <div className="url-search-field">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search URL or title..."
                  value={searchTerm}
                  onChange={(event) => {
                    const val = event.target.value;
                    searchRef.current = val;
                    setSearchTerm(val);
                    clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => {
                      setCurrentPage(1);
                      fetchUrls(1, val);
                    }, 400);
                  }}
                />
              </div>

            </div>

            <Link
              to="/url-settings"
              className="btn btn-primary url-settings-btn"
            >
              Settings
            </Link>
          </div>
        </section>

        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: "100%" }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 110 }} />
              </colgroup>

              <thead>
                <tr>
                  <th className="text-center">#</th>
                  <th>URL</th>
                  <th className="url-mobile-hide">Last Crawl Time</th>
                  <th className="url-mobile-hide">Document</th>
                  <th className="url-mobile-hide">Chunks</th>
                  <th className="url-mobile-hide text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedUrls.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">
                      No URLs found.
                    </td>
                  </tr>
                ) : (
                  paginatedUrls.map((item, index) => {
                    const statusMeta = getStatusMeta(item);
                    const isRefreshing = refreshingIds.includes(item.url_pid);
                    const isDeleting = deletingIds.includes(item.url_pid);

                    return (
                      <tr className="history-row" key={item.url_pid}>
                        <td className="url-index text-center">
                          {pageStartIndex + index + 1}
                        </td>
                        <td className="url-main-cell">
                          {/* URL + title — always visible */}
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="url-link"
                            title={item.url}
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square"></i>
                            <span>{item.url}</span>
                          </a>
                          {item.page_title && (
                            <div className="url-page-title">
                              {item.page_title.replace(/\n/g, " ")}
                            </div>
                          )}
                          {item.err_msg && (
                            <div className="url-error">
                              <i className="fa-solid fa-circle-exclamation"></i>
                              {item.err_msg}
                            </div>
                          )}
                          {/* Status badge shown INSIDE main cell on mobile */}
                          <div className="url-mobile-status">
                            <span className={`url-status-badge ${statusMeta.className}`}>
                              <i className={`fa-solid ${statusMeta.icon}`}></i>
                              {statusMeta.label}
                            </span>
                          </div>
                        </td>
                        <td className="url-crawl-time url-mobile-hide">
                          {item.last_crawl_at
                            ? new Date(item.last_crawl_at).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                  hour12: false,
                                },
                              )
                            : "-"}
                        </td>
                        <td className="url-mobile-hide">
                          {item.doc_pid ? (
                            <Link
                              to={`/knowledge-base-details/${item.doc_pid}`}
                              className="url-document-link"
                            >
                              Yes
                            </Link>
                          ) : (
                            <span className="text-muted">No</span>
                          )}
                        </td>
                        <td className="url-mobile-hide">
                          {item.chunks ?? 0}
                        </td>
                        <td className="url-mobile-hide text-center">
                          <div className="url-action-btn-group" style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary url-refresh-btn"
                              disabled={!item.doc_pid || isRefreshing}
                              style={
                                !item.doc_pid
                                  ? { opacity: 0.35, pointerEvents: "none" }
                                  : undefined
                              }
                              onClick={() => handleRefreshOne(item.url_pid)}
                              aria-label={`Refresh ${item.url}`}
                              title={item.doc_pid ? "Refresh URL" : "No document to refresh yet"}
                            >
                              <i className={`fa-solid fa-rotate ${isRefreshing ? "fa-spin" : ""}`}></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger url-delete-btn"
                              disabled={isDeleting}
                              onClick={() => setDeleteTarget(item)}
                              aria-label={`Delete ${item.url}`}
                              title="Delete URL"
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStartIndex + 1}-
                {Math.min(pageStartIndex + itemsPerPage, totalCount)}{" "}
                of {totalCount}
              </p>
              <nav aria-label="URL list pagination">
                <ul className="url-pagination mb-0">
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {paginationItems.map((item) => (
                    <li key={item}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">...</span>
                      ) : (
                        <button
                          type="button"
                          className={`url-page-btn ${
                            item === currentPage ? "active" : ""
                          }`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>

      <DeleteConfirmModal
        show={!!deleteTarget}
        type="URL"
        value={deleteTarget?.url}
        warning="This will also delete the related knowledge base document and its chunks, if any."
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </main>
  );
};

export default UrlList;
